import type { EnvironmentData, EnvironmentRecordImportSummary } from "./types";

export type EnvironmentSetupResponse = {
  environment?: Partial<EnvironmentData>;
  setup?: EnvironmentData["setup"] | null;
  summary?: EnvironmentRecordImportSummary | null;
  ready?: boolean;
  retryAfterMs?: number | null;
  readiness?: {
    ready?: boolean;
    setup?: EnvironmentData["setup"] | null;
    summary?: EnvironmentRecordImportSummary | null;
    retryAfterMs?: number | null;
    [key: string]: unknown;
  } | null;
};

export type EnvironmentSetupProgress = {
  environment: EnvironmentData;
  setup: EnvironmentData["setup"] | null;
  summary: EnvironmentRecordImportSummary | null;
  ready: boolean;
  retryAfterMs: number | null;
};

export type EnvironmentSetupRequestError = {
  status?: unknown;
  body?: unknown;
  retryAfterMs?: unknown;
};

class EnvironmentSetupTerminalError extends Error {
  constructor(setup: EnvironmentData["setup"]) {
    super(
      setup?.errorMessage?.trim() ||
        "Environment setup failed before all records were imported.",
    );
    this.name = "EnvironmentSetupTerminalError";
  }
}

function environmentSetupFailedError(setup: EnvironmentData["setup"]): Error {
  return new EnvironmentSetupTerminalError(setup);
}

export type EnvironmentSetupPollInput = {
  environment: EnvironmentData;
  /** Abort a superseded workspace-opening attempt without waiting for timeout. */
  signal?: AbortSignal;
  request: (
    attempt: number,
    signal?: AbortSignal,
  ) => Promise<EnvironmentSetupResponse>;
  onProgress?: (progress: EnvironmentSetupProgress) => void;
  onRetry?: (input: { attempt: number; error: unknown }) => void;
  isTransientError?: (error: unknown) => boolean;
  maxAttempts?: number;
  maxDurationMs?: number;
  pollIntervalMs?: number;
  retryDelayMs?: number;
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  now?: () => number;
};

function environmentSetupAbortError(): Error {
  const error = new Error("Environment setup polling was aborted.");
  error.name = "AbortError";
  return error;
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw environmentSetupAbortError();
  }
}

function abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(environmentSetupAbortError());
      return;
    }
    const timeout = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    const abort = () => {
      clearTimeout(timeout);
      signal.removeEventListener("abort", abort);
      reject(environmentSetupAbortError());
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}

function setupIsRunning(setup: EnvironmentData["setup"] | null): boolean {
  return setup?.lifecycleStatus === "running";
}

function setupHasFailed(setup: EnvironmentData["setup"] | null): boolean {
  return setup?.lifecycleStatus === "failed";
}

function hasRecordFailures(
  setup:
    | Pick<EnvironmentRecordImportSummary, "failedRecords" | "canceledRecords">
    | null
    | undefined,
): boolean {
  return Boolean(
    setup && (setup.failedRecords > 0 || setup.canceledRecords > 0),
  );
}

function setupHasTerminalLifecycleFailure(
  setup: EnvironmentData["setup"] | null,
): boolean {
  return setupHasFailed(setup);
}

function importsAreActive(
  summary: EnvironmentRecordImportSummary | null,
): boolean {
  return Boolean(
    summary && (summary.awaitingRecords > 0 || summary.activeImports > 0),
  );
}

function numericRetryAfter(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.round(value))
    : null;
}

function environmentSetupTimeoutError(maxDurationMs: number): Error {
  return new Error(`Environment setup timed out after ${maxDurationMs}ms.`);
}

export function environmentSetupProgressFromResponse(
  environment: EnvironmentData,
  response: EnvironmentSetupResponse,
  retryAfterMs?: number | null,
): EnvironmentSetupProgress {
  const readiness = response.readiness;
  const nextEnvironment: EnvironmentData = {
    ...environment,
    ...(response.environment || {}),
  };
  const explicitReady =
    typeof readiness?.ready === "boolean"
      ? readiness.ready
      : typeof response.ready === "boolean"
        ? response.ready
        : undefined;
  const hasResponseSetup = Object.prototype.hasOwnProperty.call(
    response,
    "setup",
  );
  const hasReadinessSetup =
    readiness !== null &&
    typeof readiness === "object" &&
    Object.prototype.hasOwnProperty.call(readiness, "setup");
  const hasEnvironmentSetup =
    response.environment !== null &&
    typeof response.environment === "object" &&
    Object.prototype.hasOwnProperty.call(response.environment, "setup");
  const setup = hasResponseSetup
    ? (response.setup ?? null)
    : hasReadinessSetup
      ? (readiness?.setup ?? null)
      : hasEnvironmentSetup
        ? (response.environment?.setup ?? null)
        : explicitReady === true
          ? null
          : (nextEnvironment.setup ?? null);
  nextEnvironment.setup = setup;
  const summary = response.summary ?? readiness?.summary ?? null;
  const retry = [
    retryAfterMs,
    response.retryAfterMs,
    readiness?.retryAfterMs,
  ].find((value) => typeof value === "number" && Number.isFinite(value));
  const recordFailures = hasRecordFailures(setup) || hasRecordFailures(summary);
  return {
    environment: nextEnvironment,
    setup,
    summary,
    ready:
      explicitReady !== false &&
      !setupIsRunning(setup) &&
      !setupHasFailed(setup) &&
      (explicitReady === true || !recordFailures) &&
      !importsAreActive(summary),
    retryAfterMs: numericRetryAfter(retry),
  };
}

export function environmentSetupProgressFromError(
  environment: EnvironmentData,
  error: unknown,
): EnvironmentSetupProgress | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as EnvironmentSetupRequestError;
  if (
    (candidate.status !== 503 && candidate.status !== 202) ||
    typeof candidate.body !== "string"
  ) {
    return null;
  }
  try {
    const response = JSON.parse(candidate.body) as EnvironmentSetupResponse;
    const explicitReady =
      response?.readiness?.ready ??
      (typeof response?.ready === "boolean" ? response.ready : undefined);
    if (candidate.status === 202) {
      const progress = environmentSetupProgressFromResponse(
        environment,
        response,
        numericRetryAfter(candidate.retryAfterMs),
      );
      return explicitReady === undefined
        ? { ...progress, ready: false }
        : progress;
    }
    return explicitReady === false
      ? environmentSetupProgressFromResponse(
          environment,
          response,
          numericRetryAfter(candidate.retryAfterMs),
        )
      : null;
  } catch {
    return null;
  }
}

function defaultTransientError(error: unknown): boolean {
  if (!error) return false;
  const status =
    typeof error === "object"
      ? (error as EnvironmentSetupRequestError).status
      : undefined;
  if (typeof status === "number") {
    return status === 408 || status === 425 || status === 429 || status >= 500;
  }
  const message = error instanceof Error ? error.message : String(error);
  return /failed to fetch|network error|network connection lost|connection (?:reset|closed|refused)|econnreset|socket hang up|socket connection was closed unexpectedly|dns|timed out/i.test(
    message,
  );
}

export async function pollEnvironmentSetup(
  input: EnvironmentSetupPollInput,
): Promise<EnvironmentData> {
  const maxAttempts = Math.max(1, input.maxAttempts ?? 3);
  const maxDurationMs = Math.max(1, input.maxDurationMs ?? 120_000);
  const sleep =
    input.sleep ||
    ((ms: number, signal?: AbortSignal) =>
      abortableSleep(ms, signal || new AbortController().signal));
  const now = input.now || (() => Date.now());
  const isTransientError = input.isTransientError || defaultTransientError;
  // Always pass a signal to request callbacks. The internal controller keeps
  // the callback contract stable even when callers do not need cancellation.
  const internalController = input.signal ? null : new AbortController();
  const signal = input.signal || internalController!.signal;
  const startedAt = now();
  let currentEnvironment = input.environment;
  let transientFailures = 0;
  let attempt = 0;

  const waitForNextAttempt = async (requestedDelayMs: number) => {
    throwIfAborted(signal);
    const remainingMs = maxDurationMs - Math.max(0, now() - startedAt);
    if (remainingMs <= 0) {
      throw environmentSetupTimeoutError(maxDurationMs);
    }
    await sleep(Math.min(Math.max(0, requestedDelayMs), remainingMs), signal);
    throwIfAborted(signal);
  };

  const requestWithinDeadline = async (
    requestAttempt: number,
  ): Promise<EnvironmentSetupResponse> => {
    throwIfAborted(signal);
    const remainingMs = maxDurationMs - Math.max(0, now() - startedAt);
    if (remainingMs <= 0) {
      throw environmentSetupTimeoutError(maxDurationMs);
    }

    const requestController = new AbortController();
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let abortHandler: (() => void) | null = null;
    const requestPromise = Promise.resolve().then(() =>
      input.request(requestAttempt, requestController.signal),
    );
    const timeoutPromise = new Promise<never>((_, reject) => {
      if (!Number.isFinite(remainingMs)) return;
      timeoutHandle = setTimeout(
        () => {
          reject(environmentSetupTimeoutError(maxDurationMs));
          requestController.abort();
        },
        Math.max(1, Math.ceil(remainingMs)),
      );
    });
    const abortPromise = new Promise<never>((_, reject) => {
      abortHandler = () => {
        requestController.abort();
        reject(environmentSetupAbortError());
      };
      signal.addEventListener("abort", abortHandler, { once: true });
    });

    try {
      return await Promise.race([requestPromise, timeoutPromise, abortPromise]);
    } finally {
      if (timeoutHandle !== null) {
        clearTimeout(timeoutHandle);
      }
      if (abortHandler !== null) {
        signal.removeEventListener("abort", abortHandler);
      }
    }
  };

  while (true) {
    throwIfAborted(signal);
    if (now() - startedAt >= maxDurationMs) {
      throw environmentSetupTimeoutError(maxDurationMs);
    }
    attempt += 1;
    try {
      const progress = environmentSetupProgressFromResponse(
        currentEnvironment,
        await requestWithinDeadline(attempt),
      );
      throwIfAborted(signal);
      currentEnvironment = progress.environment;
      input.onProgress?.(progress);
      if (setupHasTerminalLifecycleFailure(progress.setup) && !progress.ready) {
        throw environmentSetupFailedError(progress.setup);
      }
      if (progress.ready) return progress.environment;
      transientFailures = 0;
      await waitForNextAttempt(
        progress.retryAfterMs ?? input.pollIntervalMs ?? 1_000,
      );
    } catch (error) {
      throwIfAborted(signal);
      // A terminal setup lifecycle is authoritative even when its diagnostic
      // text resembles a retryable network failure. Do not let the generic
      // transport retry classifier reopen a failed setup run.
      if (error instanceof EnvironmentSetupTerminalError) {
        throw error;
      }
      const pending = environmentSetupProgressFromError(
        currentEnvironment,
        error,
      );
      if (pending) {
        currentEnvironment = pending.environment;
        input.onProgress?.(pending);
        if (setupHasTerminalLifecycleFailure(pending.setup) && !pending.ready) {
          throw environmentSetupFailedError(pending.setup);
        }
        if (pending.ready) return pending.environment;
        transientFailures = 0;
        await waitForNextAttempt(
          pending.retryAfterMs ?? input.pollIntervalMs ?? 1_000,
        );
        continue;
      }

      if (!isTransientError(error) || transientFailures + 1 >= maxAttempts) {
        throw error;
      }
      transientFailures += 1;
      input.onRetry?.({ attempt: transientFailures, error });
      const retryAfterMs = numericRetryAfter(
        error && typeof error === "object"
          ? (error as EnvironmentSetupRequestError).retryAfterMs
          : null,
      );
      await waitForNextAttempt(
        retryAfterMs ?? (input.retryDelayMs ?? 1_500) * transientFailures,
      );
    }
  }
}
